import Papa from 'papaparse'
import { readSheet } from 'read-excel-file/browser'
import type { RawRow } from '../domain/catalog'

export interface ParsedCatalog {
  headers: string[]
  rows: RawRow[]
  sheetName?: string
}

const MAX_FILE_SIZE = 20 * 1024 * 1024
const MAX_ROWS = 25_000
const MAX_COLUMNS = 100

const guardParsedCatalog = (catalog: ParsedCatalog): ParsedCatalog => {
  if (catalog.rows.length > MAX_ROWS) throw new Error(`The MVP supports up to ${MAX_ROWS.toLocaleString()} rows.`)
  if (catalog.headers.length > MAX_COLUMNS) throw new Error(`The MVP supports up to ${MAX_COLUMNS} columns.`)
  if (!catalog.headers.length) throw new Error('No header row was found.')
  return catalog
}

export async function parseCatalogFile(file: File): Promise<ParsedCatalog> {
  if (file.size > MAX_FILE_SIZE) throw new Error('The file is larger than the 20 MB MVP limit.')
  const extension = file.name.split('.').pop()?.toLowerCase()

  if (extension === 'csv' || extension === 'txt') {
    const text = await file.text()
    const result = Papa.parse<RawRow>(text, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header) => header.trim(),
    })
    if (result.errors.length && !result.data.length) throw new Error(result.errors[0].message)
    const headers = result.meta.fields ?? []
    return guardParsedCatalog({ headers, rows: result.data })
  }

  if (extension === 'xlsx') {
    const worksheet = await readSheet(file)
    if (!worksheet.length) throw new Error('The workbook does not contain any rows.')
    const headers = worksheet[0].map((cell) => String(cell ?? '').trim())
    const rows = worksheet.slice(1).map((cells) =>
      Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? ''])),
    )
    return guardParsedCatalog({ headers, rows, sheetName: 'First worksheet' })
  }

  throw new Error('Use a CSV or XLSX file. Legacy XLS files are not supported in the MVP.')
}
